import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { fetchMe } from "./api/home-api";
import type { MeResponse } from "./types";
import { messages } from "@/shared/i18n";

// =============================================================================
// Types
// =============================================================================

export const AUTH_STATUS = {
  PENDING: "pending",
  AUTHENTICATED: "authenticated",
  UNAUTHENTICATED: "unauthenticated",
} as const;

export type AuthStatus = (typeof AUTH_STATUS)[keyof typeof AUTH_STATUS];

interface UserContextValue {
  user: MeResponse | null;
  status: AuthStatus;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// =============================================================================
// Context
// =============================================================================

const UserContext = createContext<UserContextValue | null>(null);

// =============================================================================
// Provider
// =============================================================================

/**
 * UserProvider — egyszer hívja a GET /v1/users/me végpontot mountoláskor,
 * majd context-en keresztül megosztja az eredményt.
 *
 * - A ProtectedRoute wrappeli az Outlet-et ezzel a providerrel.
 * - useAuthGuard és useMe egyaránt ebből a context-ből olvas,
 *   így megszűnik a duplikált API hívás.
 * - A refetch függvény manuális frissítésre (pl. profil update után).
 */
export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<MeResponse | null>(null);
  const [status, setStatus] = useState<AuthStatus>(AUTH_STATUS.PENDING);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUser = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchMe();
      setUser(data);
      setStatus(AUTH_STATUS.AUTHENTICATED);
    } catch (err) {
      setUser(null);
      setStatus(AUTH_STATUS.UNAUTHENTICATED);
      const message = err instanceof Error ? err.message : messages.errors.default;
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    fetchMe()
      .then((data) => {
        if (cancelled) return;
        setUser(data);
        setStatus(AUTH_STATUS.AUTHENTICATED);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setUser(null);
        setStatus(AUTH_STATUS.UNAUTHENTICATED);
        const message = err instanceof Error ? err.message : messages.errors.default;
        setError(message);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <UserContext.Provider value={{ user, status, isLoading, error, refetch: fetchUser }}>
      {children}
    </UserContext.Provider>
  );
}

// =============================================================================
// Hook
// =============================================================================

/**
 * useUserContext — olvasó hook a UserContext-hez.
 *
 * @throws Ha UserProvider-en kívül hívják.
 */
export function useUserContext(): UserContextValue {
  const ctx = useContext(UserContext);

  if (!ctx) {
    throw new Error("useUserContext must be used within a <UserProvider>");
  }

  return ctx;
}
